import type { CampaignPurpose, WhatsAppTemplate } from '@workspace/types';
import {
  CAMPAIGN_TEMPLATE_VARIABLE_SOURCE_LABELS,
  COBRANZAS_TEMPLATE_VARIABLE_SOURCES,
  NOTICE_TEMPLATE_VARIABLE_SOURCES,
  extractTemplateVariableIndices,
} from '@workspace/utils';

type TemplateLike = Pick<WhatsAppTemplate, 'name' | 'status' | 'components' | 'variableKeys'>;

const MAX_SLOTS = 20;
// El link al chat solo existe en las campañas; un aviso no lo genera.
const NOT_IN_NOTICES = new Set<string>(['campaign.accessUrl']);

/**
 * Cantidad de variables ({{1}}, {{2}}...) que hay que completar a mano. Es 0 cuando la
 * plantilla se creo en la plataforma y ya sabe que dato va en cada una.
 */
export function noticeTemplateSlots(template: TemplateLike | undefined): number {
  if (!template || template.variableKeys.length > 0) {
    return 0;
  }
  const indices = extractTemplateVariableIndices(template.components ?? []);
  return Math.min(indices.length > 0 ? Math.max(...indices) : 0, MAX_SLOTS);
}

export function noticeVariableOptions(purpose: CampaignPurpose): Array<{ key: string; label: string }> {
  const sources =
    purpose === 'COBRANZAS'
      ? [...COBRANZAS_TEMPLATE_VARIABLE_SOURCES, ...NOTICE_TEMPLATE_VARIABLE_SOURCES]
      : NOTICE_TEMPLATE_VARIABLE_SOURCES;
  const seen = new Set<string>();
  const options: Array<{ key: string; label: string }> = [];
  for (const key of sources) {
    if (seen.has(key) || NOT_IN_NOTICES.has(key)) {
      continue;
    }
    seen.add(key);
    options.push({ key, label: CAMPAIGN_TEMPLATE_VARIABLE_SOURCE_LABELS[key] });
  }
  return options;
}

/** Todas las variables tienen un dato elegido y ese dato sirve para el proposito. */
export function noticeVariablesReady(slots: number, chosen: string[], purpose: CampaignPurpose): boolean {
  if (slots === 0) {
    return true;
  }
  const allowed = new Set(noticeVariableOptions(purpose).map(option => option.key));
  return chosen.length >= slots && chosen.slice(0, slots).every(key => allowed.has(key));
}

/**
 * Una entrada por nombre de plantilla (la misma plantilla aparece una vez por idioma),
 * prefiriendo la que ya esta aprobada.
 */
export function uniqueNoticeTemplates<T extends TemplateLike>(templates: T[]): T[] {
  const byName = new Map<string, T>();
  for (const template of templates.slice(0, 500)) {
    const current = byName.get(template.name);
    if (!current || (current.status !== 'APPROVED' && template.status === 'APPROVED')) {
      byName.set(template.name, template);
    }
  }
  return [...byName.values()];
}
